import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type { LeagueSummaryDTO } from '@pickem/shared';
import { AuthShell } from '../components/AuthShell';
import { FocusShell } from '../components/Layout';
import { Spinner, Card, ErrorBanner, Field } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';

type Preview =
  | { status: 'valid'; email: string; leagueName: string; hasAccount: boolean }
  | { status: 'expired' | 'accepted' | 'notfound' };

type Stage = 'loading' | 'register' | 'success' | 'error';

export function StaffInviteAcceptPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [stage, setStage] = useState<Stage>('loading');
  const [leagueName, setLeagueName] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', password: '', phone: '' });
  const [busy, setBusy] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!token || loading || started.current) return;
    started.current = true;

    (async () => {
      // Already signed in → accept directly (existing behavior).
      if (user) {
        try {
          const { leagueId } = await api.post<{ leagueId: string }>(`/leagues/staff-invite/${token}/accept`);
          try {
            const ctx = await api.get<{ leagues: LeagueSummaryDTO[] }>('/leagues');
            setLeagueName(ctx.leagues.find((l) => l.id === leagueId)?.name ?? null);
          } catch { /* non-fatal */ }
          await refresh();
          setStage('success');
        } catch (err) {
          setErrorMessage(err instanceof ApiError ? err.message : 'Unable to accept this invitation.');
          setStage('error');
        }
        return;
      }

      // Not signed in → look up the invite and pick a screen.
      try {
        const p = await api.get<Preview>(`/invites/${token}`);
        if (p.status === 'valid' && !p.hasAccount) {
          setLeagueName(p.leagueName);
          setInviteEmail(p.email);
          setStage('register');
        } else if (p.status === 'valid' && p.hasAccount) {
          navigate(`/login?next=${encodeURIComponent(`/staff-invite/${token}`)}`, { replace: true });
        } else if (p.status === 'expired') {
          setErrorMessage('This invitation has expired. Ask a league admin to send a new one.');
          setStage('error');
        } else if (p.status === 'accepted') {
          setErrorMessage('This invitation has already been used. Try signing in.');
          setStage('error');
        } else {
          setErrorMessage('This invitation could not be found.');
          setStage('error');
        }
      } catch (err) {
        setErrorMessage(err instanceof ApiError ? err.message : 'Unable to load this invitation.');
        setStage('error');
      }
    })();
  }, [token, loading, user, refresh, navigate]);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function onRegister(e: React.FormEvent) {
    e.preventDefault();
    setErrorMessage('');
    setBusy(true);
    try {
      await api.post(`/invites/${token}/register`, {
        password: form.password,
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone || undefined,
      });
      await refresh();
      navigate('/admin', { replace: true });
    } catch (err) {
      setErrorMessage(err instanceof ApiError ? err.message : 'Unable to create your account.');
    } finally {
      setBusy(false);
    }
  }

  if (stage === 'loading') {
    return (
      <FocusShell>
        <Spinner label="Loading your invitation…" />
      </FocusShell>
    );
  }

  if (stage === 'register') {
    return (
      <AuthShell
        title="Accept your invitation"
        subtitle={`You've been invited to help run ${leagueName ?? 'a league'}. Create your account to get started.`}
        footer={
          <>
            Already have an account?{' '}
            <Link to={`/login?next=${encodeURIComponent(`/staff-invite/${token}`)}`} className="font-medium text-grove underline">
              Sign in
            </Link>
          </>
        }
      >
        <form onSubmit={onRegister} noValidate>
          {errorMessage && (
            <div className="mb-4">
              <ErrorBanner message={errorMessage} />
            </div>
          )}
          <Field label="Email" htmlFor="invite-email">
            <input id="invite-email" className="input" value={inviteEmail} readOnly disabled autoComplete="email" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="First name" htmlFor="firstName">
              <input id="firstName" required className="input" autoComplete="given-name" value={form.firstName} onChange={(e) => set('firstName', e.target.value)} />
            </Field>
            <Field label="Last name" htmlFor="lastName">
              <input id="lastName" required className="input" autoComplete="family-name" value={form.lastName} onChange={(e) => set('lastName', e.target.value)} />
            </Field>
          </div>
          <Field label="Phone" htmlFor="phone" hint="Optional. US 10-digit number.">
            <input id="phone" type="tel" className="input" autoComplete="tel" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field label="Password" htmlFor="password" hint="At least 8 characters.">
            <input id="password" type="password" required minLength={8} className="input" autoComplete="new-password" value={form.password} onChange={(e) => set('password', e.target.value)} />
          </Field>
          <button type="submit" className="btn-primary btn-lg w-full" disabled={busy}>
            {busy ? 'Creating account…' : 'Create account & accept'}
          </button>
        </form>
      </AuthShell>
    );
  }

  if (stage === 'success') {
    return (
      <FocusShell>
        <Card className="mt-2 flex flex-col items-center gap-3 px-6 py-10 text-center">
          <img src="/brand/favicon.svg" alt="" aria-hidden className="h-12 w-12" />
          <h1 className="font-display text-3xl uppercase text-grove">
            You&rsquo;re now staff{leagueName ? ` of ${leagueName}` : ''}!
          </h1>
          <button className="btn-primary mt-2" onClick={() => navigate('/admin')}>
            Go to Admin
          </button>
        </Card>
      </FocusShell>
    );
  }

  return (
    <FocusShell>
      <div className="mt-2 space-y-4">
        <ErrorBanner message={errorMessage} />
        <div className="text-center">
          <Link to="/login" className="text-sm text-grove underline">
            Go to sign in
          </Link>
        </div>
      </div>
    </FocusShell>
  );
}
